import { Alert, Button, Stack, Text } from '@chakra-ui/react'
import CustomTooltip from 'brokertslib/src/components/CustomTooltip'
import { flexGroupListContext } from 'brokertslib/src/context'
import {
  useAddFlexGroupUser,
  useGetAll,
  useGetByUserId,
  useRemoveFlexGroupUser,
} from 'brokertslib/src/hooks/useFlexGroup'
import { noop } from 'lodash'

import { useAuth } from '@/context/AuthContext'
import { Add, Close, Refresh } from '@/icons'

import {
  FlexGroupList,
  FlexGroupListDefaultColumns,
} from '../flex-group/FlexGroupList'
import { SectionHeading } from '../shared/SectionHeading'

export const UserFlexGroups = ({ userId }: { userId: string }) => {
  const auth = useAuth()
  const {
    isLoading,
    isRefetching,
    isSuccess,
    isError,
    data: assignedData,
    refetch: refetchAssigned,
  } = useGetByUserId(userId)

  const {
    isLoading: isAllLoading,
    isRefetching: isAllRefetching,
    isSuccess: isAllSuccess,
    isError: isAllError,
    data: allData,
    refetch: refetchAll,
  } = useGetAll({
    showAllItems: true,
    showInvalid: false,
    pageIndex: 1,
  })

  const { mutateAsync: removeFlexGroupUserAsync } = useRemoveFlexGroupUser()
  const { mutateAsync: addFlexGroupUserAsync } = useAddFlexGroupUser()

  const removeFlexGroupUser = (flexGroupId: number) => {
    removeFlexGroupUserAsync({ userId, flexGroupId }).then(() =>
      refetchAssigned(),
    )
  }

  const addFlexGroupUser = (flexGroupId: number) => {
    addFlexGroupUserAsync({ userId, flexGroupId }).then(() => refetchAssigned())
  }

  return (
    <>
      <Stack>
        <SectionHeading>Přidělené flexibilní skupiny</SectionHeading>

        <flexGroupListContext.Provider
          value={{
            loading: isLoading || isRefetching,
            totalCount: assignedData?.data.totalCount ?? 0,
            currentPage: 1,
            pageSize: 10,
            filter: { showInvalid: false, pageIndex: 1 },
            setCurrentPage: noop,
            setPageSize: noop,
            updateFilter: noop,
            reload: () => refetchAssigned(),
          }}
        >
          {(isSuccess || isLoading) && (
            <FlexGroupList
              flexGroups={assignedData?.data.items ?? []}
              columnsDefinition={[
                ...FlexGroupListDefaultColumns,
                {
                  key: 'actions',
                  name: '',
                  cellExpr: (item) =>
                    auth?.user?.allowEditOrganizationStructure && (
                      <CustomTooltip label="Odebrat" hasArrow>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFlexGroupUser(item.id)}
                          px={1}
                        >
                          <Close />
                        </Button>
                      </CustomTooltip>
                    ),
                  cellProps: { textAlign: 'right', px: 2 },
                },
              ]}
            />
          )}
          {isError && (
            <Alert
              status="error"
              p={8}
              alignItems="center"
              justifyContent="center"
            >
              <Stack gap={4}>
                <Text fontSize="lg" fontWeight="semibold">
                  Nepodařilo se načíst data
                </Text>
                <Button
                  colorScheme="secondary"
                  onClick={() => refetchAssigned()}
                >
                  <Refresh />
                  Načíst znovu
                </Button>
              </Stack>
            </Alert>
          )}
        </flexGroupListContext.Provider>
      </Stack>
      <Stack>
        <SectionHeading>Přidat skupinu</SectionHeading>

        <flexGroupListContext.Provider
          value={{
            loading: isAllLoading || isAllRefetching,
            totalCount: allData?.data.totalCount ?? 0,
            currentPage: 1,
            pageSize: 10,
            filter: { showInvalid: false, pageIndex: 1 },
            setCurrentPage: noop,
            setPageSize: noop,
            updateFilter: noop,
            reload: () => refetchAssigned(),
          }}
        >
          {(isAllSuccess || isAllLoading) && (
            <FlexGroupList
              flexGroups={
                allData?.data.items.filter(
                  (x) => !assignedData?.data.items.some((y) => y.id === x.id),
                ) ?? []
              }
              columnsDefinition={[
                ...FlexGroupListDefaultColumns,
                {
                  key: 'actions',
                  name: '',
                  cellExpr: (item) =>
                    auth?.user?.allowEditOrganizationStructure && (
                      <CustomTooltip label="Přidat" hasArrow>
                        <Button
                          variant="ghost"
                          size="sm"
                          px={1}
                          onClick={() => addFlexGroupUser(item.id)}
                        >
                          <Add />
                        </Button>
                      </CustomTooltip>
                    ),
                  cellProps: { textAlign: 'right', px: 2 },
                },
              ]}
            />
          )}
          {isAllError && (
            <Alert
              status="error"
              p={8}
              alignItems="center"
              justifyContent="center"
            >
              <Stack gap={4}>
                <Text fontSize="lg" fontWeight="semibold">
                  Nepodařilo se načíst data
                </Text>
                <Button colorScheme="secondary" onClick={() => refetchAll()}>
                  <Refresh />
                  Načíst znovu
                </Button>
              </Stack>
            </Alert>
          )}
        </flexGroupListContext.Provider>
      </Stack>
    </>
  )
}
